import React, { useEffect, useState } from "react";
import { Button, Space, Typography, message, Tag, Spin, Modal } from "antd";
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import { apiRequests } from "src/shared/api/api";
import { useMutation, useQuery, useQueryClient } from "react-query";

const { Title } = Typography;

const WorkTimer = () => {
  const queryClient = useQueryClient();
  const [blockShiftActions, setBlockShiftActions] = useState(false);
  const [blockBreakActions, setBlockBreakActions] = useState(false);

  const { data: shiftStatus, isLoading } = useQuery(
    "shiftStatus",
    async () => {
      if (blockShiftActions) return;
      const res = await apiRequests.shifts.getStatus();
      if (res.status === 422) throw new Error("422");
      return res.data;
    },
    {
      refetchInterval: 1000 * 60 * 5,
      retry: false,
      onError: (err) => {
        handleQueryError(err, setBlockShiftActions);
      },
    }
  );

  const [shiftActive, setShiftActive] = useState(false);
  const [workSeconds, setWorkSeconds] = useState(0);
  const [breakSeconds, setBreakSeconds] = useState(0);
  const [breakActive, setBreakActive] = useState(false);
  const [breakDuration, setBreakDuration] = useState(null);
  const [breakProgress, setBreakProgress] = useState(0);

  useEffect(() => {
    if (!shiftStatus) return;

    const isOnShift = shiftStatus.is_on_shift;
    const isOnBreak = shiftStatus.status === "on_break";

    setShiftActive(isOnShift);
    setBreakActive(isOnBreak);

    if (isOnShift && shiftStatus.current_shift) {
      const shiftStart = new Date(
        shiftStatus.current_shift.shift_start + "Z"
      ).getTime();
      const now = Date.now();

      if (isOnBreak) {
        const breakStart = new Date(
          shiftStatus.current_shift.break_start + "Z"
        ).getTime();
        const breakSeconds = Math.floor((now - breakStart) / 1000);
        setBreakSeconds(breakSeconds);

        const serverBreakDuration = shiftStatus.current_shift.break_duration;
        setBreakDuration(serverBreakDuration);

        if (serverBreakDuration) {
          const progress = Math.min(
            100,
            (breakSeconds / (serverBreakDuration * 60)) * 100
          );
          setBreakProgress(progress);
        }

        const breakDurationMs = serverBreakDuration || 0;
        setWorkSeconds(
          Math.floor((now - shiftStart - breakDurationMs * 60 * 1000) / 1000)
        );
      } else {
        setWorkSeconds(Math.floor((now - shiftStart) / 1000));
        setBreakSeconds(0);
        setBreakProgress(0);
        setBreakDuration(null);
      }
    } else {
      setWorkSeconds(0);
      setBreakSeconds(0);
      setBreakActive(false);
      setBreakProgress(0);
      setBreakDuration(null);
    }

    setBlockShiftActions(false);
    setBlockBreakActions(false);
  }, [shiftStatus]);

  useEffect(() => {
    if (!shiftActive) return;
    const interval = setInterval(() => {
      if (breakActive) {
        setBreakSeconds((prev) => {
          const newValue = prev + 1;
          if (breakDuration) {
            const progress = Math.min(
              100,
              (newValue / (breakDuration * 60)) * 100
            );
            setBreakProgress(progress);
          }
          return newValue;
        });
      } else {
        setWorkSeconds((prev) => prev + 1);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [breakActive, shiftActive, breakDuration]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
  };

  const handleError = (error, setBlock) => {
    if (!error.response) {
      message.error("Ошибка сети");
      return;
    }
    const status = error.response.status;
    const msg =
      error.response.data?.detail || error.message || "Ошибка сервера";
    message.error(msg);
    if (status === 422) setBlock(true);
  };

  const handleQueryError = (error, setBlock) => {
    if (error.message === "422") {
      setBlock(true);
      Modal.error({
        title: "Ошибка",
        content: "Невозможно получить статус смены",
      });
      return;
    }
    handleError(error, setBlock);
  };

  const startShiftMutation = useMutation(
    () => apiRequests.shifts.startShift({}),
    {
      onSuccess: () => queryClient.invalidateQueries("shiftStatus"),
      onError: (err) => handleError(err, setBlockShiftActions),
    }
  );

  const endShiftMutation = useMutation(() => apiRequests.shifts.endShift({}), {
    onSuccess: () => queryClient.invalidateQueries("shiftStatus"),
    onError: (err) => handleError(err, setBlockShiftActions),
  });

  const startBreakMutation = useMutation(
    (minutes) => {
      setBreakDuration(minutes);
      return apiRequests.shifts.createBreak({ duration_minutes: minutes });
    },
    {
      onSuccess: () => queryClient.invalidateQueries("shiftStatus"),
      onError: (err) => handleError(err, setBlockBreakActions),
    }
  );

  const endBreakMutation = useMutation(() => apiRequests.shifts.endBreak(), {
    onSuccess: () => {
      setBreakDuration(null);
      setBreakProgress(0);
      queryClient.invalidateQueries("shiftStatus");
    },
    onError: (err) => handleError(err, setBlockBreakActions),
  });

  const handleBreakAction = (minutes) => {
    if (!shiftActive || blockBreakActions) return;

    if (breakActive) {
      endBreakMutation.mutate();
    } else {
      startBreakMutation.mutate(minutes);
    }
  };

  const handleOpenShift = () => {
    if (blockShiftActions) return;
    startShiftMutation.mutate();
  };

  const handleCloseShift = () => {
    if (blockShiftActions) return;
    endShiftMutation.mutate();
  };

  const getStatusColor = () => {
    if (!shiftActive) return "#d9d9d9";
    return breakActive ? "#ff7a45" : "#1890ff"; // Яркий оранжевый для перерыва
  };

  const getStatusText = () => {
    if (!shiftActive) return "Смена не активна";
    return breakActive ? "На перерыве" : "В работе";
  };

  const getTimerBackground = () => {
    if (!shiftActive) return "#fafafa";
    return breakActive ? "#fff2e8" : "#e6f7ff"; // Светло-оранжевый для перерыва
  };

  // Цвета для кнопок
  const getButtonColors = (isActiveBreak, minutes) => {
    const isCurrentActiveBreak = breakActive && breakDuration === minutes;

    if (isCurrentActiveBreak) {
      return {
        borderColor: "#ff7a45",
        color: "#ff7a45",
        backgroundColor: "rgba(255, 122, 69, 0.1)",
        progressColor: "rgba(255, 122, 69, 0.2)",
      };
    }

    return {
      borderColor: breakActive ? "#ff9c6e" : "#69c0ff",
      color: breakActive ? "#ff9c6e" : "#69c0ff",
      backgroundColor: "transparent",
      progressColor: "rgba(105, 192, 255, 0.1)",
    };
  };

  if (isLoading)
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "20px" }}
      >
        <Spin size="large" />
      </div>
    );

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {/* Статус и таймер */}
      <div style={{ textAlign: "center" }}>
        <Tag
          color={shiftActive ? (breakActive ? "orange" : "blue") : "default"}
          style={{
            borderRadius: 12,
            fontWeight: 500,
            padding: "4px 12px",
          }}
        >
          {getStatusText()}
        </Tag>
        <div
          style={{
            padding: "20px",
            borderRadius: "12px",
            border: `2px solid ${getStatusColor()}`,
            backgroundColor: getTimerBackground(),
            margin: "16px 0",
            transition: "all 0.3s ease",
          }}
        >
          <Title
            level={1}
            style={{ margin: 0, fontSize: "2.5em", color: getStatusColor() }}
          >
            {formatTime(breakActive ? breakSeconds : workSeconds)}
          </Title>
        </div>
      </div>

      {/* Кнопки перерыва */}
      {shiftActive && (
        <Space.Compact style={{ width: "100%", gap: 8 }}>
          {[15, 30].map((minutes) => {
            const colors = getButtonColors(breakActive, minutes);
            const isCurrentActiveBreak =
              breakActive && breakDuration === minutes;

            return (
              <Button
                key={minutes}
                icon={
                  isCurrentActiveBreak ? (
                    <PlayCircleOutlined />
                  ) : (
                    <PauseCircleOutlined />
                  )
                }
                onClick={() => handleBreakAction(minutes)}
                disabled={
                  blockBreakActions ||
                  (breakActive && breakDuration !== minutes)
                }
                style={{
                  flex: 1,
                  position: "relative",
                  overflow: "hidden",
                  height: 40,
                  borderColor: colors.borderColor,
                  color: colors.color,
                  backgroundColor: colors.backgroundColor,
                  borderRadius: 8,
                  fontWeight: 500,
                  transition: "all 0.2s ease",
                }}
              >
                {isCurrentActiveBreak ? (
                  <>
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: "100%",
                        width: `${breakProgress}%`,
                        backgroundColor: colors.progressColor,
                        transition: "width 1s ease",
                        borderRadius: "inherit",
                      }}
                    />
                    <span
                      style={{
                        position: "relative",
                        zIndex: 1,
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {Math.round(breakProgress)}%
                    </span>
                  </>
                ) : (
                  `${minutes} мин`
                )}
              </Button>
            );
          })}
        </Space.Compact>
      )}

      {/* Кнопка смены */}
      <Button
        icon={<PoweroffOutlined />}
        type={shiftActive ? "primary" : "default"}
        danger={shiftActive}
        onClick={shiftActive ? handleCloseShift : handleOpenShift}
        disabled={blockShiftActions}
        size="large"
        block
        style={{
          height: 48,
          borderRadius: 8,
          fontWeight: 500,
        }}
      >
        {shiftActive ? "Закрыть смену" : "Открыть смену"}
      </Button>
    </Space>
  );
};

export default WorkTimer;
